import { useTranslation } from 'react-i18next';
import { Download, Landmark, Lock } from 'lucide-react';

import { useFetchCapitalSummary } from '@/hooksApi';
import { fetchCapitalStatement } from '@/api/capitalService';
import { downloadFile } from '@/utils/helpers/fileHelpers';
import { CapitalAdvanceSummary } from '@/types';

const formatIQD = (value: number) => `${value.toLocaleString()} IQD`;

const STATUS_STYLES: Record<CapitalAdvanceSummary['status'], string> = {
  scheduled: 'bg-amber-50 text-amber-700 border-amber-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  settled: 'bg-gray-100 text-gray-600 border-gray-200',
  cancelled: 'bg-red-50 text-red-600 border-red-200',
};

/**
 * Boxy Capital tab — transparency, not origination (PRD §6): the merchant
 * sees the advance, its schedule, exactly how much each installment is
 * covered, and WHY withdrawals are paused — never a silent hold.
 */
const CapitalTab = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useFetchCapitalSummary();

  const advance = data?.object ?? null;

  return isLoading ? (
    <div className='py-10 text-center text-sm text-gray-400'>{String(t('loading'))}</div>
  ) : !advance ? (
    <div className='py-10 text-center text-sm text-gray-400'>{String(t('capital_no_advance'))}</div>
  ) : (
    <div className='flex flex-col gap-4 pt-4'>
      {advance.remittance_locked ? (
        <div className='flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800'>
          <Lock className='mt-0.5 h-4 w-4 shrink-0' />
          <span>{String(t('capital_remittance_locked'))}</span>
        </div>
      ) : null}

      <div className='rounded-xl border border-gray-200 bg-white p-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <Landmark className='h-4 w-4 text-gray-500' />
            <span className='text-sm font-semibold'>{advance.provider}</span>
          </div>
          <span
            className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[advance.status]}`}
          >
            {String(t(`capital_status_${advance.status}`))}
          </span>
        </div>

        <div className='mt-4'>
          <div className='flex justify-between text-xs text-gray-500'>
            <span>{String(t('capital_repaid'))}</span>
            <span>
              {formatIQD(advance.covered)} / {formatIQD(advance.total_due)}
            </span>
          </div>
          <div className='mt-1 h-2 w-full overflow-hidden rounded-full bg-gray-100'>
            <div
              className='h-full rounded-full bg-emerald-500'
              style={{ width: `${Math.min(100, advance.progress * 100)}%` }}
            />
          </div>
        </div>

        <div className='mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4'>
          <div>
            <div className='text-xs text-gray-500'>{String(t('capital_principal'))}</div>
            <div className='font-semibold'>{formatIQD(advance.principal)}</div>
          </div>
          <div>
            <div className='text-xs text-gray-500'>{String(t('capital_fee'))}</div>
            <div className='font-semibold'>{formatIQD(advance.commission)}</div>
          </div>
          <div>
            <div className='text-xs text-gray-500'>{String(t('capital_uncovered'))}</div>
            <div className='font-semibold'>{formatIQD(advance.uncovered)}</div>
          </div>
          <div>
            <div className='text-xs text-gray-500'>{String(t('capital_transferred'))}</div>
            <div className='font-semibold'>{formatIQD(advance.transferred)}</div>
          </div>
        </div>
      </div>

      <div className='rounded-xl border border-gray-200 bg-white'>
        <div className='flex items-center justify-between border-b border-gray-100 px-4 py-3'>
          <span className='text-sm font-semibold'>{String(t('capital_schedule'))}</span>
          <button
            type='button'
            onClick={() =>
              downloadFile('', () => fetchCapitalStatement(), `boxy-capital-statement-${advance.uid}.csv`)
            }
            className='inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-50'
          >
            <Download className='h-3.5 w-3.5' />
            {String(t('capital_export_statement'))}
          </button>
        </div>
        <div className='overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='text-left text-xs text-gray-500'>
                <th className='px-4 py-2 font-medium'>{String(t('capital_due_date'))}</th>
                <th className='px-4 py-2 font-medium'>{String(t('amount'))}</th>
                <th className='px-4 py-2 font-medium'>{String(t('capital_covered'))}</th>
              </tr>
            </thead>
            <tbody>
              {advance.schedule.map((row) => (
                <tr key={row.due_date} className='border-t border-gray-50'>
                  <td className='px-4 py-2'>{row.due_date}</td>
                  <td className='px-4 py-2'>{formatIQD(row.amount)}</td>
                  <td className='px-4 py-2'>
                    <span
                      className={
                        row.covered >= row.amount
                          ? 'text-emerald-600'
                          : row.covered > 0
                            ? 'text-amber-600'
                            : 'text-gray-400'
                      }
                    >
                      {formatIQD(row.covered)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CapitalTab;
